import {
  getConsentFromStorage,
  has,
  saveConsentToStorage,
  type AllConsentNames,
  type ConsentState,
} from 'c15t';

// `ConsentInfo` isn't exported from `c15t`'s package root (only from an
// internal subpath), so its shape is captured structurally off the one public
// function that takes it.
type StoredConsentInfo = Parameters<
  typeof saveConsentToStorage
>[0]['consentInfo'];

/**
 * Read-only consent enforcement for the legacy SPA.
 *
 * belong-web mounts no banner and no consent provider. It does not need to:
 * `next.config.js` proxies every unmatched path here through a server-side
 * `fallback` rewrite, so the browser only ever sees `belonghome.com`. The `c15t`
 * cookie written by the marketing pages is therefore same-origin and directly
 * readable, and `getConsentFromStorage` prioritises the cookie over
 * localStorage precisely so this kind of cross-app read works.
 *
 * The practical consequence: a visitor reaches an account page only after
 * passing through a marketing page, so a choice already exists. When it does
 * not — a direct link, a cleared cookie — we deny everything non-essential and
 * let the footer link on the marketing side be the way back in. Failing closed
 * is the whole point.
 */
type StoredConsent = {
  consents?: Partial<ConsentState>;
  consentInfo?: StoredConsentInfo;
};

/**
 * Every category defaults to denied. `getConsentFromStorage` normalises stored
 * values to explicit booleans, but a *missing* record has to be treated as a
 * refusal rather than as an absence of information.
 */
const DENY_ALL: ConsentState = {
  necessary: true,
  functionality: false,
  experience: false,
  measurement: false,
  marketing: false,
};

function hasGpcSignal(): boolean {
  const gpcNavigator = window.navigator as Navigator & {
    globalPrivacyControl?: boolean | '1';
  };

  return (
    gpcNavigator.globalPrivacyControl === true ||
    gpcNavigator.globalPrivacyControl === '1'
  );
}

function readConsentState(): ConsentState {
  if (typeof window === 'undefined') {
    return DENY_ALL;
  }

  const gpcDenials = hasGpcSignal()
    ? { marketing: false, measurement: false }
    : undefined;

  try {
    const stored = getConsentFromStorage<StoredConsent>();

    if (!stored?.consents) {
      return DENY_ALL;
    }

    return { ...DENY_ALL, ...stored.consents, ...gpcDenials };
  } catch {
    // Private-mode storage errors, a malformed cookie — anything unreadable
    // means we have no consent on record.
    return DENY_ALL;
  }
}

export function applyGlobalPrivacyControl(): void {
  if (typeof window === 'undefined' || !hasGpcSignal()) {
    return;
  }

  try {
    const stored = getConsentFromStorage<StoredConsent>();

    if (!stored?.consents) {
      return;
    }

    const { marketing, measurement } = stored.consents;

    if (!marketing && !measurement) {
      return;
    }

    saveConsentToStorage({
      consents: { ...stored.consents, marketing: false, measurement: false },
      consentInfo: stored.consentInfo ?? { time: Date.now() },
    });
  } catch (error) {
    // The rewrite is what carries the opt-out back to the marketing app;
    // `readConsentState` already denies both categories here regardless.
    console.warn('Failed to persist Global Privacy Control opt-out', error);
  }
}

/**
 * Whether the visitor has granted the given category.
 *
 * Read at call time rather than cached: the consent cookie can change in
 * another tab on the marketing side while this SPA is open, and this app has no
 * subscription to that.
 */
export function hasConsent(category: AllConsentNames): boolean {
  return has(category, readConsentState());
}

/**
 * c15t category -> Google Consent Mode v2 signal mapping.
 *
 * Mirrors the mapping passed to c15t's Google helpers in
 * `apps/belong-next/src/features/consent/constants.ts`. The two must agree: a
 * visitor moving from a marketing page into the SPA has to see the same consent
 * state applied to the same Google container.
 */
const CONSENT_MODE_MAPPING: Record<AllConsentNames, string[]> = {
  necessary: ['security_storage'],
  functionality: ['functionality_storage'],
  experience: ['personalization_storage'],
  measurement: ['analytics_storage'],
  marketing: ['ad_storage', 'ad_user_data', 'ad_personalization'],
};

/**
 * Pushes the denied Google Consent Mode v2 default, then the stored consent as
 * an `update`, mirroring belong-next's `google-consent-mode.ts`.
 *
 * Must run before the GTM container loads (`helpers/GoogleTagManager.js`
 * injects it from an effect, so module-scope in `index.js` is early enough).
 */
export function applyStoredConsentModeDefaults(): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('consent', 'default', {
    security_storage: 'granted',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('set', 'ads_data_redaction', true);
  window.gtag('set', 'url_passthrough', true);

  const state = readConsentState();

  if (!state.measurement && !state.marketing) {
    return;
  }

  const signals: Record<string, 'granted' | 'denied'> = {};

  for (const [category, googleSignals] of Object.entries(
    CONSENT_MODE_MAPPING,
  )) {
    const granted = state[category as AllConsentNames] === true;

    for (const signal of googleSignals) {
      signals[signal] = granted ? 'granted' : 'denied';
    }
  }

  window.gtag('consent', 'update', signals);
  window.gtag('set', 'ads_data_redaction', signals.ad_storage === 'denied');
}
